import styled from 'styled-components';
import { media, Row, Column } from '@simplywallst/ui-core';
import InsightsLogo from '@assets/images/logo/market-insights.svg';

export const Main = styled.main`
  background-color: ${({ theme }) => theme.color.white};
  color: ${({ theme }) => theme.color.black};
  width: 100%;
`;

export const HeaderContainer = styled.header`
  display: flex;
  padding-top: ${({ theme }) => theme.x4spacePx}px;
  ${media.mdDown`
    padding-top: 0;
    flex-direction: column;
  `}
`;

export const InnerContainer = styled.section`
  position: relative;
  max-width: ${({ theme }) => theme.xlMin};
  width: 100%;
  padding-top: ${({ theme }) => theme.x4spacePx}px;
  ${media.mdDown`
    padding-top: 0;
    margin: 0 ${({ theme }) => theme.x4spacePx}px;
  `}
  > footer {
    ${media.lgUp`
      padding-left: ${({ theme }) => theme.x12spacePx}px;
      padding-top: ${({ theme }) => theme.x8spacePx}px;
  `}
    padding: ${({ theme }) => theme.x4spacePx}px 0;
  }
`;

export const LogoCol = styled.div`
  display: flex;
  flex: 0 0 ${({ theme }) => theme.x12spacePx}px;
  justify-content: center;
  ${media.mdDown`
    display: none;
  `}
`;

export const BodyCol = styled(Column)`
  overflow-x: auto;
  ${media.lgUp`
    flex: 0 0 calc(75% - ${({ theme }) => theme.x12spacePx}px);
    `}
`;

export const MainContainer = styled(Row)`
  justify-content: center;
  max-width: ${({ theme }) => theme.xlMin};
  position: relative;
  margin: 0 auto;
  ${media.mdDown`
    padding-top: 0;
    margin: 0 ${({ theme }) => theme.x4spacePx}px;
  `}
`;

export const SidebarCol = styled(Column)`
  ${media.lgUp`
    padding-right: ${({ theme }) => theme.x2spacePx}px;
  `}
`;

export const BreadcrumbWrapper = styled.div`
  flex: 1;
  margin-bottom: ${({ theme }) => theme.x1spacePx}px;
  ${media.mdDown`
    padding-top: ${({ theme }) => theme.x4spacePx}px;
  `}
`;

export const StyledInsightsLogo = styled(InsightsLogo)`
  ${media.mdDown`
    display: none;
  `}
`;

export const MobileInsightsLogoWrapper = styled.div`
  height: ${({ theme }) => theme.x5spacePx}px;
  background: ${({ theme }) => theme.color.chart02};

  svg {
    margin-left: ${({ theme }) => theme.x2spacePx}px;
    margin-top: ${({ theme }) => theme.x1spacePx}px;
  }

  ${media.lgUp`
    display: none;
  `}

  ${media.mdDown`
    margin: 0 -${({ theme }) => theme.x4spacePx}px;
  `}
`;

export const Content = styled('div')`
  padding-right: ${({ theme }) => theme.x8spacePx}px;
  ${media.mdDown`
    padding-right: 0;
  `}
  ol {
    list-style-type: decimal;
  }
  ul {
    list-style-type: disc;
  }
  ol,
  ul {
    margin: ${({ theme }) => theme.x2spacePx * 1.25}px 0
      ${({ theme }) => theme.x1spacePx * 1.5}px
      ${({ theme }) => theme.x2spacePx * 1.25}px;
    li {
      ${({ theme }) => theme.typography.base}
      color: ${(props) => props.theme.color.spaceGray};
      margin: ${({ theme }) => theme.x1spacePx}px;
      strong {
        font-weight: 700;
      }
    }
  }
  > table {
    table-layout: auto;
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
    border: 1px solid #ddd;
    ${({ theme }) => theme.typography.base}
    color: ${(props) => props.theme.color.spaceGray};
    margin-top: ${({ theme }) => theme.x4spacePx}px;
    margin-bottom: ${({ theme }) => theme.x4spacePx}px;
    display: block;
    max-width: fit-content;
    margin: 0 auto;
    overflow-x: auto;
    white-space: nowrap;
    th {
      padding: ${({ theme }) => theme.x1spacePx}px
        ${({ theme }) => theme.x2spacePx}px;
    }
    td {
      padding: ${({ theme }) => theme.x1spacePx}px;
      strong {
        font-weight: 700;
      }
    }
    tr:nth-child(odd) {
      background-color: ${(props) => props.theme.color.spaceGrayRGBA(0.2)};
    }
  }
  > p {
    ${({ theme }) => theme.typography.base}
    color: ${(props) => props.theme.color.spaceGray};
    margin-top: ${({ theme }) => theme.x4spacePx}px;
    i {
      ${({ theme }) => theme.typography.small}
      font-style: italic;
      strong {
        ${(props) => props.theme.typography.smallStrong}
      }
    }
    strong {
      font-weight: 700;
    }
    a {
      color: ${(props) => props.theme.color.black};
      background-color: ${(props) => props.theme.color.brand01RGBA(0.1)};
      text-decoration: underline;
      &:hover {
        color: ${(props) => props.theme.color.blackRGBA(0.7)};
      }
    }
  }
  > h3 {
    margin-top: ${({ theme }) => theme.x4spacePx}px;
    margin-bottom: ${({ theme }) => theme.x2spacePx}px;
  }
  > figure {
    display: flex;
    flex-direction: column;
    align-items: center;
    div {
      width: 100%;
    }
    figcaption {
      ${(props) => props.theme.typography.small}
      text-align: center;
      color: ${(props) => props.theme.color.spaceGrayRGBA(0.5)};
    }
  }
  color: ${(props) => props.theme.color.spaceGray};
`;

export const MetadataSection = styled.section`
  display: flex;
  margin-top: ${({ theme }) => theme.x2spacePx}px;
  ${media.xsDown`
    flex-direction: column;
  `}
`;

export const Avatar = styled.img`
  height: ${({ theme }) => theme.x4spacePx}px;
  width: ${({ theme }) => theme.x4spacePx}px;
  border-radius: 50%;
  vertical-align: middle;
`;

export const StyledLink = styled('a')`
  ${({ theme }) => theme.typography.smallStrong}
  color: ${(props) => props.theme.color.spaceGrayRGBA(0.7)};
  margin-left: ${({ theme }) => theme.x1spacePx}px;
  text-decoration: none;
  &:hover {
    color: ${(props) => props.theme.color.brand01RGBA(0.8)};
    text-decoration: underline;
  }
`;

export const PublishedDate = styled.span`
  ${({ theme }) => theme.typography.small}
  color: ${(props) => props.theme.color.spaceGrayRGBA(0.7)};
  margin-top: ${({ theme }) => theme.x2spacePx}px;
  display: block;
`;

export const Feedback = styled.p`
  && {
    ${(props) => props.theme.typography.small}
    padding-top: 30px;
    border-top: 2px solid ${(props) => props.theme.color.lightGray};
  }
`;

export const IntersectionBox = styled('span')`
  height: 1px;
  display: block;
`;

export const ShareSection = styled(Row)`
  margin: ${({ theme }) => theme.x4spacePx}px 0;
  button,
  p {
    margin-right: ${({ theme }) => theme.x2spacePx}px;
  }
`;

export const CommentsLink = styled('a')`
  ${(props) => props.theme.typography.small};
  color: ${(props) => props.theme.color.brand01};
  text-decoration: none;

  ${media.smUp`
    margin-left: ${({ theme }) => theme.x2spacePx}px;
  `}
  ${media.xsOnly`
    margin: 0;
    margin-top: ${({ theme }) => theme.x1spacePx}px;
  `}
`;

export const RegisterButtonWrapper = styled('div')`
  border-left: 4px solid;
  border-color: ${({ theme }) => theme.color.brand01};
  padding-left: ${({ theme }) => theme.x3spacePx}px;
  margin-bottom: ${({ theme }) => theme.x3spacePx}px;
  margin-top: ${({ theme }) => theme.x4spacePx}px;
`;

export const RegisterHeadingWrapper = styled('div')`
  display: flex;
  margin-bottom: ${({ theme }) => theme.x1spacePx}px;
`;

export const SubscribeButton = styled('a')`
  ${({ theme }) => theme.typography.xSmall};
  background-color: ${({ theme }) => theme.color.brand01};
  border-radius: 6px;
  color: ${({ theme }) => theme.color.white};
  padding: ${({ theme }) => `6px ${theme.x3spacePx}px`};
  text-decoration: none;
  font-size: ${({ theme }) => theme.typography.base};
`;

export const SubscribeButtonWrapper = styled('div')`
  margin-top: ${({ theme }) => theme.x1spacePx}px;
`;

export const IconWrapper = styled('div')`
  padding-right: ${({ theme }) => theme.x1spacePx}px;
`;

export const ReviewedByWrapper = styled('div')`
  p {
    margin-left: ${({ theme }) => theme.x1spacePx}px;
  }

  a {
    ${(props) => props.theme.typography.xSmall};
    color: ${(props) => props.theme.color.spaceGrayRGBA(0.7)};
    text-decoration: none;
    &:hover {
      color: ${(props) => props.theme.color.brand01RGBA(0.8)};
      text-decoration: underline;
    }
  }
`;

export const VideoContainer = styled('div')`
  overflow: hidden;
  padding-bottom: 56.25%;
  position: relative;
  height: 0;

  > iframe {
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    position: absolute;
  }
`;
